import React, { useState } from "react";
import {
  Box,
  Paper,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * Large tappable option for onboarding-style flows.
 * Defaults: `Paper` (outlined) + `ListItemButton` (`selected`, hover, focus from the theme).
 * Optional `loading`: disables the control, hides label copy (layout preserved), shows centered spinner.
 * Optional `expandable` (horizontal only): hides the inline description, surfaces it on
 * hover via a tooltip, and adds an accordion-style arrow that reveals it inline.
 */
const SelectableChoiceCard = ({
  icon = null,
  title,
  description,
  onClick,
  selected = false,
  disabled = false,
  loading = false,
  orientation = "horizontal",
  expandable = false,
  action = null,
  sx: sxProp,
}) => {
  const vertical = orientation === "vertical";
  const [expanded, setExpanded] = useState(false);

  const collapsible = expandable && Boolean(description) && !vertical;
  const showInlineDescription = Boolean(description) && !collapsible;

  const button = (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      sx={[
        { position: "relative", width: "100%", p: 2 },
        ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
      ]}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: vertical ? "column" : "row",
          alignItems: vertical ? "center" : "flex-start",
          width: "100%",
          visibility: loading ? "hidden" : "visible",
        }}
      >
        {icon ? (
          <ListItemIcon
            sx={{
              color: "primary.main",
              ...(vertical && { minWidth: 0, justifyContent: "center" }),
            }}
          >
            {icon}
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={title}
          secondary={showInlineDescription ? description : undefined}
          primaryTypographyProps={{
            variant: "subtitle1",
            fontWeight: 700,
            ...(vertical && { textAlign: "center" }),
          }}
          secondaryTypographyProps={
            vertical ? { textAlign: "center" } : undefined
          }
          sx={{ margin: 0 }}
        />
        {action || collapsible ? (
          <Box
            sx={{
              ml: "auto",
              alignSelf: "center",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {action}
            {collapsible ? (
              <IconButton
                aria-label={expanded ? "Hide description" : "Show description"}
                aria-expanded={expanded}
                disabled={disabled || loading}
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: (theme) =>
                    theme.transitions.create("transform", {
                      duration: theme.transitions.duration.shortest,
                    }),
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            ) : null}
          </Box>
        ) : null}
      </Box>
      {loading ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <CircularProgress size={28} color="primary" />
        </Box>
      ) : null}
    </ListItemButton>
  );

  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: selected ? "primary.main" : "divider" }}
    >
      {collapsible ? (
        <Tooltip
          title={<Typography variant="subtitle1">{description}</Typography>}
          placement="top"
          arrow
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}
      {collapsible ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Collapse>
      ) : null}
    </Paper>
  );
};

export default SelectableChoiceCard;
